import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, Paper, Button, Toolbar, IconButton, Divider, Grid, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Menu, MenuItem } from '@mui/material';
import RadialGauge from './components/RadialGauge';
import { Save as SaveIcon, Undo as UndoIcon, Redo as RedoIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, GridOn as GridIcon, Preview as PreviewIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { toast } from 'react-toastify';
import pageService from './services/pageService';
import { useLocation, useHistory } from 'react-router-dom';
import SimplePropertyInspector from './SimplePropertyInspector';
import Draggable from 'react-draggable';

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function PageDesignerRGL() {
  const location = useLocation();
  const history = useHistory();
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pageName, setPageName] = useState('');
  const [widgets, setWidgets] = useState([]); // meta: {id, name, component, props}
  const [selectedId, setSelectedId] = useState(null);
  const [layouts, setLayouts] = useState({ lg: [] });
  const [editingPageId, setEditingPageId] = useState(null);
  const [currentBp, setCurrentBp] = useState('lg');
  const [pageSize, setPageSize] = useState({ width: 1200, height: 800 });
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, id: null });
  const [clipboard, setClipboard] = useState(null);

  // ✨ RMMS Pixel-Perfect Responsive
  const [containerWidth, setContainerWidth] = useState(1200);

  // Dynamic cols calculation (RMMS style - all breakpoints use actual width)
  const cols = useMemo(() => ({
    lg: containerWidth,
    md: containerWidth,
    sm: containerWidth,
    xs: containerWidth,
    xxs: containerWidth
  }), [containerWidth]);

  const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
  const marginX = 0, marginY = 0; // ✨ Changed from 10 to 0 for pixel-perfect
  const rowHeight = 1; // ✨ Changed from 20 to 1 for pixel-perfect (1 grid unit = 1 pixel)

  // ✨ Track container width for responsive scaling (RMMS style)
  useEffect(() => {
    const updateContainerWidth = () => {
      // Calculate available width (similar to RMMS DynPage.js)
      const aside = document.querySelector('aside');
      const sidebarWidth = aside?.offsetWidth || 0;

      // Available width = window width - sidebar width
      const availableWidth = window.innerWidth - sidebarWidth;

      setContainerWidth(availableWidth > 0 ? availableWidth : 1200);
    };

    // Initial calculation
    updateContainerWidth();

    // Update on window resize
    window.addEventListener('resize', updateContainerWidth);

    // Cleanup
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  // Keyboard shortcuts for arrow key movement
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't interfere with input fields
      if (e.target.tagName.toLowerCase() === 'input' || e.target.tagName.toLowerCase() === 'textarea') {
        return;
      }

      // Arrow keys - move selected widget by 1px (or 10px with Shift)
      if (selectedId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const delta = e.shiftKey ? 10 : 1;

        setLayouts(prev => {
          const next = { ...prev };
          const arr = [...((next[currentBp] || next.lg) || [])];
          const idx = arr.findIndex(l => l.i === selectedId);
          if (idx >= 0) {
            const current = arr[idx];
            let { x, y } = current;
            switch (e.key) {
              case 'ArrowUp': y = Math.max(0, y - delta); break;
              case 'ArrowDown': y = y + delta; break;
              case 'ArrowLeft': x = Math.max(0, x - delta); break;
              case 'ArrowRight': x = x + delta; break;
              default: break;
            }
            arr[idx] = { ...current, x, y };
          }
          next[currentBp] = arr;
          return next;
        });

        // Update widget _updateKey for visual feedback
        setWidgets(prev => prev.map(w =>
          w.id === selectedId ? { ...w, _updateKey: Date.now() } : w
        ));
      }

      // Delete key
      if (e.key === 'Delete' && selectedId) {
        e.preventDefault();
        setWidgets(prev => prev.filter(w => w.id !== selectedId));
        setLayouts(prev => ({ ...prev, [currentBp]: (prev[currentBp] || []).filter(l => l.i !== selectedId) }));
        setSelectedId(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, currentBp]);

  const widgetLibrary = useMemo(() => [
    { id: 'text-label', name: 'Text Label', icon: '🏷️', component: 'TextLabel' },
    { id: 'text-box', name: 'Text Box', icon: '📝', component: 'TextBox' },
    { id: 'button', name: 'Button', icon: '🔘', component: 'Button' },
    { id: 'led-indicator', name: 'LED Indicator', icon: '💡', component: 'LEDIndicator' },
    { id: 'switch', name: 'Switch', icon: '⏻', component: 'Switch' },
    { id: 'progress-bar', name: 'Progress Bar', icon: '📊', component: 'ProgressBar' },
    { id: 'gauge', name: 'Gauge', icon: '⏲️', component: 'Gauge' },
    { id: 'radial-gauge', name: 'Radial Gauge', icon: '🎯', component: 'Gauge', initialProps: { variant: 'dial', label: 'Gauge' } },
    { id: 'trend-chart', name: 'Trend Chart', icon: '📈', component: 'TrendChart' },
    { id: 'alarm-list', name: 'Alarm List', icon: '🚨', component: 'AlarmList' }
  ], []);

  const defaultPropsFor = useCallback((component) => {
    switch (component) {
      case 'TextLabel':
        return { text: 'Label', foreColor: '#1f2937', fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 12, fontWeight: 500, italic: false, textAlign: 'center', tag: '' };
      case 'TextBox':
        return { placeholder: 'Text...', foreColor: '#111827', fontSize: 12, textAlign: 'left', borderColor: '#cbd5e1', backgroundColor: '#ffffff' };
      case 'Button':
        return { text: 'Button', variant: 'contained', color: 'primary', size: 'small' };
      case 'LEDIndicator':
        return { on: true, size: 14, onColor: '#10b981', offColor: '#94a3b8', shape: 'circle', tag: '' };
      case 'Switch':
        return { on: false, size: 'small', onColor: '#10b981', offColor: '#94a3b8', trackColor: '#e5e7eb', tag: '' };
      case 'ProgressBar':
        return { value: 40, color: '#8e44ad', thickness: 8, direction: 'horizontal' };
      case 'Gauge':
        return { value: 65, min: 0, max: 100, variant: 'linear' /* 'linear' | 'indicator' | 'dial' */ , color: '#1976d2', tag: '', startAngle: -135, endAngle: 135, majorTicks: 5, minorTicks: 2, greenZones: [], yellowZones: [], redZones: [] };
      case 'TrendChart':
        return { source: 'tag:Temp01', range: '1h', lineColor: '#2563eb' };
      case 'AlarmList':
        return { filter: 'active', showAck: true };
      default:
        return {};
    }
  }, []);

  const addWidget = useCallback((def) => {
    const id = `${def.id}-${Date.now()}`;
    const baseProps = defaultPropsFor(def.component);
    const mergedProps = { ...baseProps, ...(def.initialProps || {}) };
    setWidgets(prev => [...prev, { id, name: def.name, component: def.component, props: mergedProps }]);
    setLayouts(prev => {
      const next = { ...prev };
      const bps = Object.keys(cols);
      for (const bp of bps) {
        const arr = [...(next[bp] || [])];
        // ✨ With pixel-perfect: default widget size in pixels
        arr.push({ i: id, x: 0, y: Infinity, w: 200, h: 100 });
        next[bp] = arr;
      }
      return next;
    });
    setSelectedId(id);
  }, [defaultPropsFor]);

  const handleSave = useCallback(() => setSaveDialogOpen(true), []);

  const handlePreview = useCallback(() => {
    if (!editingPageId) {
      toast.warning('Please save the page first before previewing');
      return;
    }
    history.push(`/axisPreview/${editingPageId}`);
  }, [editingPageId, history]);

  // Compaction davranışını dinamik yap: tek öğe varken vertical compact kapalı olsun
  const effectiveCompactType = useMemo(() => null, []);

  const findLayout = useCallback((bp, id) => {
    const arr = ((layouts[bp] || layouts.lg) || []);
    return arr.find(l => l.i === id);
  }, [layouts]);

  const bringToFront = useCallback(() => {
    setWidgets(prev => {
      const maxZ = prev.reduce((m, w) => {
        const z = (w.props && Number.isFinite(w.props.zIndex)) ? w.props.zIndex : 1;
        return Math.max(m, z);
      }, 1);
      return prev.map(w => {
        if (w.id !== ctxMenu.id) return w;
        const newProps = { ...(w.props || {}), zIndex: maxZ + 1 };
        return { ...w, props: newProps };
      });
    });
    setCtxMenu({ open: false, x: 0, y: 0, id: null });
  }, [ctxMenu.id]);

  const removeWidget = useCallback((id) => {
    setWidgets(prev => prev.filter(w => w.id !== id));
    setLayouts(prev => {
      const next = { ...prev };
      Object.keys(cols).forEach(bp => {
        next[bp] = (next[bp] || []).filter(l => l.i !== id);
      });
      return next;
    });
    if (selectedId === id) setSelectedId(null);
    setCtxMenu({ open: false, x: 0, y: 0, id: null });
  }, [selectedId]);

  const copyWidget = useCallback(() => {
    const id = ctxMenu.id;
    if (!id) return;
    const meta = widgets.find(w => w.id === id);
    const lay = findLayout(currentBp, id);
    if (!meta || !lay) { setCtxMenu({ open:false, x:0, y:0, id:null }); return; }
    setClipboard({ meta: { ...meta, id: undefined }, layout: { ...lay } });
    setCtxMenu({ open:false, x:0, y:0, id:null });
  }, [ctxMenu.id, widgets, currentBp, findLayout]);

  const pasteWidget = useCallback(() => {
    if (!clipboard) return;
    const base = clipboard.meta;
    const src = clipboard.layout;
    const newId = `${(base.component || 'widget')}-${Date.now()}`;
    setWidgets(prev => [...prev, { id: newId, name: base.name, component: base.component, props: { ...(base.props || {}) } }]);
    setLayouts(prev => {
      const next = { ...prev };
      Object.keys(cols).forEach(bp => {
        const arr = [...(next[bp] || [])];
        // ✨ With pixel-perfect: containerWidth is the max width
        const totalWidth = containerWidth;
        const w = Math.min(src.w || 120, totalWidth);
        const xRaw = (bp === currentBp ? (src.x + 10) : src.x); // Offset by 10px when pasting
        const x = Math.max(0, Math.min(xRaw, totalWidth - w));
        const y = Math.max(0, src.y);
        arr.push({ i: newId, x, y, w, h: src.h || 80 });
        next[bp] = arr;
      });
      return next;
    });
    setSelectedId(newId);
    setCtxMenu({ open:false, x:0, y:0, id:null });
  }, [clipboard, currentBp, containerWidth]);

  const toggleLock = useCallback(() => {
    const id = ctxMenu.id;
    if (!id) return;
    setLayouts(prev => {
      const next = { ...prev };
      Object.keys(cols).forEach(bp => {
        const arr = [...(next[bp] || [])];
        const idx = arr.findIndex(l => l.i === id);
        if (idx >= 0) {
          const cur = arr[idx];
          arr[idx] = { ...cur, static: !cur.static };
        }
        next[bp] = arr;
      });
      return next;
    });
    setCtxMenu({ open:false, x:0, y:0, id:null });
  }, [ctxMenu.id]);

  const align = useCallback((mode) => {
    const id = ctxMenu.id;
    if (!id) return;
    setLayouts(prev => {
      const next = { ...prev };
      const arr = [...((next[currentBp] || next.lg) || [])];
      const idx = arr.findIndex(l => l.i === id);
      if (idx >= 0) {
        const it = { ...arr[idx] };
        // ✨ With pixel-perfect: containerWidth is the total available width
        const totalWidth = containerWidth;
        if (mode === 'left') it.x = 0;
        else if (mode === 'right') it.x = Math.max(0, totalWidth - it.w);
        else if (mode === 'center') it.x = Math.max(0, Math.round((totalWidth - it.w) / 2));
        arr[idx] = it;
      }
      next[currentBp] = arr;
      return next;
    });
    setCtxMenu({ open:false, x:0, y:0, id:null });
  }, [ctxMenu.id, currentBp, containerWidth]);

  const computeAbsFor = useCallback((l) => {
    // ✨ With pixel-perfect grid (rowHeight=1, margin=0):
    // Grid coordinates ARE pixel coordinates, no conversion needed!
    return {
      x: l.x,
      y: l.y,
      w: l.w,
      h: l.h
    };
  }, []);

  const computeGridFromAbs = useCallback((abs) => {
    // ✨ With pixel-perfect grid (rowHeight=1, margin=0):
    // Pixel coordinates ARE grid coordinates, no conversion needed!
    return {
      x: Math.max(0, abs.x),
      y: Math.max(0, abs.y),
      w: Math.max(1, abs.w),
      h: Math.max(1, abs.h)
    };
  }, []);

  const computeAbsLayout = useCallback(() => {
    const arr = ((layouts[currentBp] || layouts.lg) || []);
    return arr.map(l => ({ i: l.i, ...computeAbsFor(l) }));
  }, [layouts, currentBp, computeAbsFor]);

  const handleSaveConfirm = useCallback(() => {
    if (!pageName.trim()) return;
    // Merge layout into widgets (absolute px for storage)
    const abs = computeAbsLayout();
    const absById = Object.fromEntries(abs.map(a => [a.i, a]));
    const widgetsOut = widgets.map(w => {
      const a = absById[w.id] || {};
      return { ...w, x: a.x ?? 0, y: a.y ?? 0, width: a.w ?? 120, height: a.h ?? 60 };
    });
    const payload = {
      pageName: pageName.trim(),
      description: '',
      isActive: true,
      widgets: widgetsOut,
    };
    const op = editingPageId ? pageService.update(editingPageId, payload)
                             : pageService.create(payload);
    op.then((response) => {
      // Set editingPageId if creating new page
      if (!editingPageId && response && response.id) {
        setEditingPageId(response.id);
      }
      toast.success('Page saved successfully');
      setSaveDialogOpen(false);
    })
      .catch((err) => {
        console.error('❌ Error saving page:', err);
        toast.error('Error saving page');
      });
  }, [pageName, widgets, layouts, pageSize, computeAbsLayout, editingPageId]);

  const sanitizeLayoutItem = useCallback((it) => {
    const toNum = (v, d) => {
      const n = typeof v === 'string' ? parseInt(v, 10) : v;
      return Number.isFinite(n) ? n : d;
    };
    return {
      i: String(it.i),
      x: toNum(it.x, 0),
      y: toNum(it.y, 0),
      w: toNum(it.w, 3),
      h: toNum(it.h, 4),
      static: !!it.static
    };
  }, []);

  const sanitizeLayouts = useCallback((all) => {
    const next = {};
    Object.keys(all || {}).forEach((bp) => {
      next[bp] = (all[bp] || []).map(sanitizeLayoutItem);
    });
    return next;
  }, [sanitizeLayoutItem]);

  const onLayoutChange = useCallback((current, all) => {
    setLayouts(sanitizeLayouts(all || {}));
  }, [sanitizeLayouts]);
  const onBreakpointChange = useCallback((bp) => setCurrentBp(bp), []);

  const currentMeta = useMemo(() => {
    const meta = widgets.find(w => w.id === selectedId);
    const lay = ((layouts[currentBp] || layouts.lg) || []).find(l => l.i === selectedId);
    if (!meta || !lay) return null;
    // Include tagId at top level if it exists in props or properties
    const tagId = meta.props?.tagId || meta.properties?.tagId || meta.tagId;
    return {
      id: selectedId,
      component: meta.component,
      name: meta.name,
      x: lay.x,
      y: lay.y,
      width: lay.w,
      height: lay.h,
      props: meta.props,
      ...(tagId ? { tagId } : {})
    };
  }, [widgets, layouts, selectedId, currentBp]);

  const updateLayoutFor = useCallback((id, patch) => {
    const clean = Object.fromEntries(Object.entries(patch).filter(([k,v]) => v !== undefined && v !== null && !(typeof v === 'number' && Number.isNaN(v))));
    setLayouts(prev => {
      const next = { ...prev };
      const arr = [...((next[currentBp] || next.lg) || [])];
      const idx = arr.findIndex(l => l.i === id);
      if (idx >= 0) arr[idx] = { ...arr[idx], ...clean };
      next[currentBp] = arr;
      return next;
    });
  }, [currentBp]);

  const WidgetPreview = ({ meta }) => {
    if (!meta) return null;
    const t = meta.component;
    const p = meta.props || {};
    const withBorder = (child) => (
      <Box sx={{ width:'100%', height:'100%', boxSizing:'border-box', border: (p.borderWidth ?? 0) > 0 ? `${p.borderWidth}px solid ${p.borderColor || '#94a3b8'}` : 'none', borderRadius: `${p.borderRadius || 0}px`, display:'flex', alignItems:'center', justifyContent:'center' }}>
        {child}
      </Box>
    );

    if (t === 'TextLabel') {
      const style = {
        fontFamily: p.fontFamily,
        fontSize: `${p.fontSize || 12}px`,
        fontWeight: p.fontWeight || 400,
        fontStyle: p.italic ? 'italic' : 'normal',
        color: p.color || '#111827',
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: (p.align === 'left' ? 'flex-start' : p.align === 'right' ? 'flex-end' : 'center'),
        padding: '0 4px',
        textAlign: p.align || 'center'
      };
      return withBorder(<Box sx={style}>{p.text || meta.name || 'Text'}</Box>);
    }

    if (t === 'TextBox') {
      return withBorder(
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%', height: 26, border: '1px solid', borderColor: p.borderColor || '#cbd5e1', bgcolor: p.background || '#fff', color: p.color || '#111827', fontSize: `${p.fontSize || 12}px`, px: 1, display:'flex', alignItems:'center' }}>
            <span style={{ opacity: 0.6 }}>{p.placeholder || 'Text...'}</span>
          </Box>
        </Box>
      );
    }

    if (t === 'Button') {
      return withBorder(<Button variant={p.variant || 'contained'} size={p.size || 'small'} color={p.color || 'primary'}>{p.text || meta.name || 'Button'}</Button>);
    }

    if (t === 'LEDIndicator') {
      const color = p.on ? (p.onColor || '#10b981') : (p.offColor || '#94a3b8');
      const radius = (p.shape === 'square') ? '4px' : '50%';
      return withBorder(<Box sx={{ width: p.size || 14, height: p.size || 14, borderRadius: radius, bgcolor: color, boxShadow: '0 0 8px rgba(0,0,0,0.15)' }} />);
    }

    if (t === 'Switch') {
      const on = !!p.on;
      const trackW = 38, trackH = 20;
      const knob = { size: 16, offset: on ? (trackW - 18) : 2 };
      return withBorder(
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems:'center', justifyContent:'center' }}>
          <Box sx={{ width: trackW, height: trackH, borderRadius: trackH/2, bgcolor: on ? (p.onColor || '#10b981') : (p.trackColor || '#e5e7eb'), position:'relative' }}>
            <Box sx={{ position:'absolute', top: 2, left: knob.offset, width: knob.size, height: knob.size, borderRadius: '50%', bgcolor: on ? '#fff' : (p.offColor || '#94a3b8'), transition:'left 120ms' }} />
          </Box>
        </Box>
      );
    }

    if (t === 'ProgressBar') {
      const val = Math.min(100, Math.max(0, p.value ?? 0));
      const thick = Math.max(4, p.thickness || 8);
      return withBorder(
        <Box sx={{ width: '100%', height: '100%', px: 1, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%', height: thick, bgcolor: '#eee', borderRadius: thick/2, overflow: 'hidden' }}>
            <Box sx={{ width: `${val}%`, height: '100%', bgcolor: p.color || '#8e44ad' }} />
          </Box>
        </Box>
      );
    }

    if (t === 'Gauge') {
      const val = Math.min(p.max ?? 100, Math.max(p.min ?? 0, p.value ?? 0));
      const pct = ((val - (p.min ?? 0)) / ((p.max ?? 100) - (p.min ?? 0))) * 100;
      if (p.variant === 'dial') {
        return withBorder(
          <RadialGauge
            value={p.value ?? 0}
            min={p.min ?? 0}
            max={p.max ?? 100}
            label={p.label}
            majorTicks={p.majorTicks || 5}
            minorTicks={p.minorTicks || 2}
            startAngle={p.startAngle ?? -135}
            endAngle={p.endAngle ?? 135}
            greenZones={p.greenZones || []}
            yellowZones={p.yellowZones || []}
            redZones={p.redZones || []}
          />
        );
      }
      if (p.variant === 'indicator') {
        // Simple circular indicator
        const size = 64;
        const stroke = 6;
        const radius = (size - stroke) / 2;
        const circumference = 2 * Math.PI * radius;
        const dash = (pct / 100) * circumference;
        return withBorder(
          <Box sx={{ width: '100%', height: '100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column' }}>
            <svg width={size} height={size}>
              <circle cx={size/2} cy={size/2} r={radius} stroke="#e5e7eb" strokeWidth={stroke} fill="none" />
              <circle cx={size/2} cy={size/2} r={radius} stroke={p.color || '#1976d2'} strokeWidth={stroke} fill="none" strokeDasharray={`${dash} ${circumference-dash}`} transform={`rotate(-90 ${size/2} ${size/2})`} strokeLinecap="round" />
            </svg>
            <Typography variant="caption" sx={{ mt: 0.5 }}>{Math.round(pct)}%</Typography>
          </Box>
        );
      }
      // default linear
      const thick = 10;
      return withBorder(
        <Box sx={{ width: '90%', height: '100%', m:'0 auto', display:'flex', flexDirection:'column', justifyContent:'center' }}>
          <Box sx={{ width: '100%', height: thick, bgcolor: '#eee', borderRadius: thick/2, overflow:'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: p.color || '#1976d2' }} />
          </Box>
          <Typography variant="caption" sx={{ mt: 0.5, textAlign:'center' }}>{val}</Typography>
        </Box>
      );
    }

    if (t === 'TrendChart') {
      return withBorder(
        <Box sx={{ width: '100%', height: '100%', p: 1, display:'flex', alignItems:'center', justifyContent:'center', border: '1px dashed #cbd5e1', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">Trend: {p.source || 'n/a'} ({p.range || '1h'})</Typography>
        </Box>
      );
    }

    if (t === 'AlarmList') {
      return withBorder(
        <Box sx={{ width: '100%', height: '100%', p: 1, display:'flex', alignItems:'center', justifyContent:'center', border: '1px dashed #f59e0b', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">Alarm List ({p.filter || 'all'})</Typography>
        </Box>
      );
    }

    return <Typography variant="caption">{meta.name}</Typography>;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper elevation={1} sx={{ zIndex: 2 }}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ flexGrow: 1 }}>SCADA Page Designer</Typography>
          <IconButton onClick={handleSave} color="primary"><SaveIcon /></IconButton>
          <IconButton disabled><UndoIcon /></IconButton>
          <IconButton disabled><RedoIcon /></IconButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <TextField type="number" label="Page W" size="small" value={pageSize.width} onChange={e=> setPageSize(s=>({...s, width: Math.max(200, parseInt(e.target.value)||0)}))} sx={{ width: 110, mr: 1 }} />
          <TextField type="number" label="Page H" size="small" value={pageSize.height} onChange={e=> setPageSize(s=>({...s, height: Math.max(200, parseInt(e.target.value)||0)}))} sx={{ width: 110, mr: 2 }} />
          <IconButton onClick={()=> setZoom(z=> Math.max(z-10, 50))}><ZoomOutIcon /></IconButton>
          <Typography variant="body2" sx={{ mx: 1 }}>{zoom}%</Typography>
          <IconButton onClick={()=> setZoom(z=> Math.min(z+10, 200))}><ZoomInIcon /></IconButton>
          <IconButton onClick={()=> setShowGrid(s=>!s)} color={showGrid?'primary':'default'}><GridIcon /></IconButton>
          <IconButton onClick={handlePreview} color="secondary" title="Preview Page"><PreviewIcon /></IconButton>
        </Toolbar>
      </Paper>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Paper elevation={1} sx={{ width: 250, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Widget Library</Typography>
            <Grid container spacing={1}>
              {widgetLibrary.map(w => (
                <Grid key={w.id} item xs={4}>
                  <Card sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={()=> addWidget(w)}>
                    <CardContent sx={{ p:0.5, textAlign:'center' }}>
                      <Typography sx={{ fontSize: 22, mb: 0.25 }}>{w.icon}</Typography>
                      <Typography variant="caption" display="block" sx={{ fontSize: 10 }}>{w.name}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, position: 'relative', overflow: 'auto' }}>
          <Box sx={{ position:'relative', width: pageSize.width, height: pageSize.height, minHeight: 200, backgroundImage: showGrid?'linear-gradient(transparent, transparent)':'none' }}>
            <ResponsiveGridLayout
              className="layout"
              layouts={layouts}
              breakpoints={breakpoints}
              cols={cols}
              rowHeight={rowHeight}
              margin={[marginX, marginY]}
              containerPadding={[marginX, marginY]}
              compactType={effectiveCompactType}
              allowOverlap
              preventCollision={false}
              onLayoutChange={onLayoutChange}
              onBreakpointChange={onBreakpointChange}
              transformScale={zoom/100}
              isResizable
              isDraggable
            >
              {widgets.map(meta => {
                const lay = findLayout(currentBp, meta.id) || { i: meta.id, x: 0, y: Infinity, w: 3, h: 4 };
                const safe = sanitizeLayoutItem(lay);
                return (
                <div
                  key={meta.id}
                  data-grid={safe}
                  onClick={(ev)=>{ ev.stopPropagation(); setSelectedId(meta.id); }}
                  onContextMenu={(ev)=>{ ev.preventDefault(); ev.stopPropagation(); setSelectedId(meta.id); setCtxMenu({ open: true, x: ev.clientX, y: ev.clientY, id: meta.id }); }}
                  style={{
                    border: selectedId===meta.id ? '2px solid #7B83FF':'1px solid rgba(0,0,0,0.1)',
                    borderRadius:4,
                    background:'#fff',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    position:'relative',
                    zIndex: (meta.props && Number.isFinite(meta.props.zIndex)) ? meta.props.zIndex : 1
                  }}
                >
                  <WidgetPreview key={`preview-${meta.id}-${meta._updateKey || 0}`} meta={meta} />
                </div>
              );})}
            </ResponsiveGridLayout>

            {/* Context Menu */}
            <Menu
              open={ctxMenu.open}
              onClose={()=> setCtxMenu({ open: false, x: 0, y: 0, id: null })}
              anchorReference="anchorPosition"
              anchorPosition={ctxMenu.open ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
            >
              <MenuItem onClick={copyWidget}>Kopyala</MenuItem>
              <MenuItem onClick={pasteWidget} disabled={!clipboard}>Yapıştır</MenuItem>
              <Divider />
              <MenuItem onClick={bringToFront}>Üste getir</MenuItem>
              <MenuItem onClick={() => align('left')}>Sola hizala</MenuItem>
              <MenuItem onClick={() => align('center')}>Ortala</MenuItem>
              <MenuItem onClick={() => align('right')}>Sağa hizala</MenuItem>
              <Divider />
              <MenuItem onClick={toggleLock}>Kilitle / Kilidi Kaldır</MenuItem>
              <MenuItem onClick={() => removeWidget(ctxMenu.id)} sx={{ color: 'error.main' }}>Sil</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Property inspector - Floating & Draggable - MOVED OUTSIDE GRID CONTAINER */}
        <Draggable
          handle=".drag-handle"
          cancel="input, button, textarea, select, .MuiAutocomplete-root, .MuiTextField-root, .react-switch"
          defaultPosition={{ x: window.innerWidth - 500, y: 16 }}
        >
          <Box sx={{
            position:'fixed',
            width: 450,
            zIndex:1000,
            bgcolor:'background.paper',
            borderRadius:1,
            boxShadow:3,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Drag Handle Header - ALWAYS ON TOP */}
            <Box className="drag-handle" sx={{
              cursor: 'move',
              bgcolor: '#edbc45',
              color: 'white',
              p: 1,
              borderRadius: '4px 4px 0 0',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              userSelect: 'none',
              flexShrink: 0
            }}>
              <DragIcon fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Widget Properties</Typography>
            </Box>
            <SimplePropertyInspector
                widget={currentMeta}
                onChange={(updated)=>{
                  // Update meta with _updateKey to force re-render
                  setWidgets(prev=> prev.map(w=> w.id===updated.id ? { ...w, props: updated.props || w.props, properties: updated.properties || w.properties, name: w.name, _updateKey: Date.now() } : w));
                  // Update layout (prefer absolute if requested)
                  if (updated.useAbs && (updated.absX!==undefined || updated.absY!==undefined || updated.absW!==undefined || updated.absH!==undefined)) {
                    const curr = ((layouts[currentBp]||layouts.lg)||[]).find(l=> l.i===updated.id) || {};
                    const abs = {
                      x: updated.absX ?? computeAbsFor(curr).x,
                      y: updated.absY ?? computeAbsFor(curr).y,
                      w: updated.absW ?? computeAbsFor(curr).w,
                      h: updated.absH ?? computeAbsFor(curr).h,
                    };
                    const grid = computeGridFromAbs(abs);
                    updateLayoutFor(updated.id, { x: grid.x, y: grid.y, w: grid.w, h: grid.h });
                  } else {
                    updateLayoutFor(updated.id, { x: updated.x, y: updated.y, w: updated.width, h: updated.height });
                  }
                }}
                onDelete={(id)=>{
                  setWidgets(prev=> prev.filter(w=> w.id!==id));
                  setLayouts(prev => ({ ...prev, [currentBp]: (prev[currentBp]||[]).filter(l=> l.i!==id) }));
                  setSelectedId(null);
                }}
              />
          </Box>
        </Draggable>
      </Box>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={()=> setSaveDialogOpen(false)}>
        <DialogTitle>Save SCADA Page</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Page Name" value={pageName} onChange={e=> setPageName(e.target.value)} margin="dense" variant="outlined" />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=> setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveConfirm} variant="contained" disabled={!pageName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Load existing page if pageId provided */}
      <LoadOnMount location={location} setEditingPageId={setEditingPageId} setPageName={setPageName} setWidgets={setWidgets} setLayouts={setLayouts} cols={cols} />
    </Box>
  );
}

function LoadOnMount({ location, setEditingPageId, setPageName, setWidgets, setLayouts, cols }) {
  useEffect(() => {
    (async () => {
      const sp = new URLSearchParams(location.search || '');
      const pid = sp.get('id') || sp.get('pageId') || sp.get('pageid');
      if (!pid) return;
      const page = await pageService.get(Number(pid));
      if (!page) return;
      setEditingPageId(Number(pid));
      setPageName(page.pageName || '');
      // Widgets are normalized by service (component/props present)
      const metas = Array.isArray(page.widgets) ? page.widgets.map(w => ({
        id: w.id || `${w.component}-${Date.now()}`,
        name: w.name || w.component,
        component: w.component,
        props: w.props || {},
        properties: w.properties || w.props || {},
        ...(w.tagId ? { tagId: w.tagId } : {}) // Include top-level tagId if it exists
      })) : [];
      setWidgets(metas);
      // Build grid layout from saved absolute positions (x, y, width, height)
      // With pixel-perfect grid (rowHeight=1, margin=0), saved values are direct pixel coordinates
      const lg = Array.isArray(page.widgets) ? page.widgets.map((w, idx) => ({
        i: w.id || `${w.component}-${Date.now()}`,
        x: typeof w.x === 'number' ? w.x : 0,
        y: typeof w.y === 'number' ? w.y : idx * 100,
        w: typeof w.width === 'number' ? w.width : 120,
        h: typeof w.height === 'number' ? w.height : 60
      })) : [];
      setLayouts({ lg });
    })();
  }, [location.search, setEditingPageId, setPageName, setWidgets, setLayouts, cols]);
  return null;
}
